import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Shield, Trash2, ChevronDown, UserPlus, X, Eye, EyeOff, KeyRound } from 'lucide-react';
import './AdminUsers.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  content_manager: 'Content Manager'
};

const ROLE_COLORS = {
  super_admin: { bg: '#fef3c7', color: '#92400e' },
  admin: { bg: '#dbeafe', color: '#1e40af' },
  content_manager: { bg: '#e0e7ff', color: '#4338ca' }
};

const AdminUsers = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newUser, setNewUser] = useState({
    full_name: '', email: '', password: '', role: 'admin'
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete "${user.full_name}" (${user.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/admin/users/${passwordTarget.id}/password`,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPasswordModal(false);
      setNewPassword('');
      setPasswordTarget(null);
      alert('Password updated successfully.');
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!newUser.full_name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      setCreateError('All fields are required.');
      return;
    }
    if (newUser.password.length < 6) {
      setCreateError('Password must be at least 6 characters.');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setNewUser({ full_name: '', email: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="admin-users"><p>Loading users...</p></div>;

  return (
    <div className="admin-users" data-testid="admin-users-page">
      <div className="users-header">
        <div>
          <h2>User Management</h2>
          <p>Manage admin users and their roles</p>
        </div>
        <button
          onClick={() => { setShowCreateModal(true); setCreateError(''); }}
          className="btn-create-user"
          data-testid="create-user-btn"
        >
          <UserPlus size={18} />
          Add Admin
        </button>
      </div>

      <div className="roles-legend">
        <div className="legend-item">
          <Shield size={14} style={{ color: '#92400e' }} />
          <strong>Super Admin</strong> — Full access to everything
        </div>
        <div className="legend-item">
          <Shield size={14} style={{ color: '#1e40af' }} />
          <strong>Admin</strong> — Manage bookings, packages, gallery, promo codes
        </div>
        <div className="legend-item">
          <Shield size={14} style={{ color: '#4338ca' }} />
          <strong>Content Manager</strong> — Manage gallery & testimonials only
        </div>
      </div>

      <div className="users-table-wrapper">
        <table className="users-table" data-testid="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const roleStyle = ROLE_COLORS[user.role] || ROLE_COLORS.content_manager;
              const isSelf = user.id === currentUser?.id;
              return (
                <tr key={user.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="user-avatar-sm">{user.full_name?.charAt(0) || '?'}</div>
                      <span>{user.full_name}</span>
                      {isSelf && <span className="you-badge">You</span>}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <div className="role-select-wrapper">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={isSelf}
                        className="role-select"
                        style={{ background: roleStyle.bg, color: roleStyle.color }}
                        data-testid={`role-select-${user.id}`}
                      >
                        <option value="super_admin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="content_manager">Content Manager</option>
                      </select>
                      <ChevronDown size={14} className="role-select-arrow" />
                    </div>
                  </td>
                  <td>
                    <span className={`status-dot ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => { setPasswordTarget(user); setShowPasswordModal(true); setNewPassword(''); setPasswordError(''); setShowNewPassword(false); }}
                        className="btn-change-password"
                        title="Change password"
                        data-testid={`change-password-${user.id}`}
                      >
                        <KeyRound size={16} />
                      </button>
                      {!isSelf && (
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="btn-delete-user"
                          title="Delete user"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)} data-testid="create-user-modal">
          <div className="modal-content create-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><UserPlus size={20} /> Create New Admin</h3>
              <button onClick={() => setShowCreateModal(false)} className="modal-close-btn" data-testid="close-create-modal">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="create-user-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. Jane Smith"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  data-testid="create-user-name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="e.g. jane@tropiluxe.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  data-testid="create-user-email"
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    data-testid="create-user-password"
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  data-testid="create-user-role"
                >
                  <option value="admin">Admin</option>
                  <option value="content_manager">Content Manager</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              {createError && <p className="form-error" data-testid="create-user-error">{createError}</p>}
              <button
                type="submit"
                disabled={creating}
                className="btn-submit-create"
                data-testid="submit-create-user"
              >
                {creating ? 'Creating...' : 'Create Admin Account'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && passwordTarget && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)} data-testid="change-password-modal">
          <div className="modal-content create-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><KeyRound size={20} /> Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="modal-close-btn" data-testid="close-password-modal">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="create-user-form">
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
                Setting new password for <strong>{passwordTarget.full_name}</strong> ({passwordTarget.email})
              </p>
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    data-testid="toggle-new-password"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {passwordError && <p className="form-error" data-testid="password-error">{passwordError}</p>}
              <button
                type="submit"
                disabled={changingPassword}
                className="btn-submit-create"
                data-testid="submit-change-password"
              >
                {changingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
